import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Activity,
  MessageSquare,
  Users,
  Shield,
  Key,
  Settings,
  UserPlus,
  UserMinus,
  Edit,
  Trash2,
  Search,
  Filter,
  Download,
  Upload,
  Eye,
  EyeOff,
  Lock,
  Unlock,
  Crown,
  Star,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Calendar,
  FileText,
  Database,
  Cloud,
  Zap,
  Globe,
  Mail,
  Phone,
  MapPin,
  Building,
  User,
  MoreVertical,
  Plus,
  RefreshCw,
  Save,
  X
} from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import NavMemoire from '../../components/memoire/NavMemoire';
import Card from '../../components/ui/Card';
import SuiviUtilisation from '../../components/assistants/SuiviUtilisation';
import CanalWhatsApp from '../../components/assistants/CanalWhatsApp';
import { assistantsService } from '../../services/api/assistants';
import Button from '../../components/ui/Button';
import CountUp from '../../components/CountUp';
import '../../styles/background-effects.css';


const AdminPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'users' | 'permissions' | 'usage' | 'whatsapp' | 'audit' | 'system'>('usage');
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState<any>(null);
  const [actionEnCours, setActionEnCours] = useState(false);
  const [messageAdmin, setMessageAdmin] = useState('');
  const [confirmationSuppression, setConfirmationSuppression] = useState(false);
  // Mot de passe provisoire à transmettre de vive voix, la messagerie n'étant
  // pas configurée.
  const [motDePasseProvisoire, setMotDePasseProvisoire] = useState<{
    email: string; motDePasse: string; envoye: boolean;
  } | null>(null);

  // États pour les données
  // Comptes réels de la plateforme. Cet écran affichait auparavant cinq
  // personnes fictives en @solutionai.com, écrites en dur : un administrateur
  // pouvait les prendre pour de vrais comptes.
  const [users, setUsers] = useState<Array<{
    id: string; name: string; email: string; role: string; status: string;
    lastLogin: string | null; department: string; position: string;
    permissions: string[]; generations: number; cout: number;
  }>>([]);

  const [permissions, setPermissions] = useState([
    { id: 'read', name: 'Lecture', description: 'Consulter les documents et données', level: 'basic' },
    { id: 'write', name: 'Écriture', description: 'Créer et modifier les documents', level: 'standard' },
    { id: 'admin', name: 'Administration', description: 'Gérer les utilisateurs et paramètres', level: 'premium' },
    { id: 'export', name: 'Export', description: 'Exporter les données', level: 'standard' },
    { id: 'api', name: 'API Access', description: 'Accès aux API', level: 'premium' },
    { id: 'audit', name: 'Audit', description: 'Consulter les logs d\'audit', level: 'premium' }
  ]);

  const [licenses, setLicenses] = useState([
    {
      id: 'basic',
      name: 'Basic',
      price: 29,
      users: 5,
      modules: ['documents'],
      features: ['Stockage 10GB', 'Support email', '2 intégrations'],
      active: true
    },
    {
      id: 'standard',
      name: 'Standard',
      price: 79,
      users: 25,
      modules: ['documents', 'content', 'teams'],
      features: ['Stockage 100GB', 'Support prioritaire', '10 intégrations', 'Analytics de base'],
      active: true
    },
    {
      id: 'premium',
      name: 'Premium',
      price: 149,
      users: 100,
      modules: ['documents', 'content', 'teams', 'analytics', 'ai'],
      features: ['Stockage illimité', 'Support 24/7', 'Intégrations illimitées', 'Analytics avancés', 'IA intégrée'],
      active: true
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: 299,
      users: -1, // illimité
      modules: ['all'],
      features: ['Tout inclus', 'Support dédié', 'SLA 99.9%', 'Sécurité avancée', 'Formation personnalisée'],
      active: true
    }
  ]);

  // Journal réel, chargé depuis le serveur. Ces lignes étaient auparavant
  // écrites en dur dans le code : elles montraient des utilisateurs et des
  // dates fictifs, ce qu'un administrateur pouvait prendre pour des faits.
  const [auditLogs, setAuditLogs] = useState<Array<{
    id: string; user: string; action: string; resource: string;
    timestamp: string; ip: string; status: string;
  }>>([]);

  // Les cartes affichaient « 0 » tant que le journal n'etait pas arrive. Un
  // zero se lit comme une mesure : l'administrateur arrivait sur un ecran
  // annoncant zero utilisateur actif, dementi par le tableau juste en dessous.
  const [journalCharge, setJournalCharge] = useState(false);

  const aujourdHui = new Date().toDateString();
  const connexionsAujourdHui = users.filter(
    (u) => u.lastLogin && new Date(u.lastLogin).toDateString() === aujourdHui,
  ).length;
  const jamaisConnectes = users.filter((u) => !u.lastLogin).length;

  useEffect(() => {
    assistantsService
      .chargerJournal()
      .then((journal) => {
        setJournalCharge(true);
        setUsers(
          journal.utilisateurs.map((u) => ({
            id: String(u.id),
            name: u.nom,
            email: u.email,
            role: u.role,
            status: u.actif ? 'active' : 'inactive',
            lastLogin: u.derniere_connexion,
            department: u.service,
            position: u.poste,
            permissions: droitsDuRole(u.role),
            generations: u.generations,
            cout: u.cout,
          })),
        );
        setAuditLogs(
          journal.traces.map((t) => ({
            id: String(t.id),
            user: t.utilisateur,
            action: t.action,
            resource: t.assistant || '—',
            timestamp: t.date,
            ip: t.ip,
            status: 'success',
          })),
        );
      })
      .catch(() => {
        setJournalCharge(true);
        setAuditLogs([]);
      });
  }, []);

  /**
   * Écrit la liste des comptes dans un fichier séparé par des points-virgules.
   *
   * Le bouton n'avait aucun gestionnaire : il ne faisait rien, et rien ne le
   * disait. L'export sert à qui doit rendre compte des accès sans avoir la
   * plateforme sous les yeux. Point-virgule et non virgule, parce que c'est ce
   * qu'attend un tableur configuré en français.
   */
  const exporterLesComptes = () => {
    const entetes = ['Nom', 'Courriel', 'Role', 'Statut', 'Service',
                     'Derniere connexion', 'Productions', 'Cout (USD)'];
    const echapper = (valeur: any) => {
      const texte = valeur === null || valeur === undefined ? '' : String(valeur);
      // Guillemets doubles : un service ou un poste peut en contenir.
      return /[";\r\n]/.test(texte) ? '"' + texte.replace(/"/g, '""') + '"' : texte;
    };
    // L'horodatage brut du serveur ne se lit ni ne se trie dans un tableur.
    const enDate = (valeur: string | null) => {
      if (!valeur) return 'Jamais connecté';
      const d = new Date(valeur);
      return Number.isNaN(d.getTime())
        ? valeur
        : d.toLocaleString('fr-FR', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
          });
    };

    const lignes = [
      entetes.join(';'),
      ...users.map((u) => [
        u.name, u.email, u.role, u.status === 'active' ? 'Actif' : 'Inactif',
        u.department || '', enDate(u.lastLogin),
        u.generations ?? 0, u.cout ?? 0,
      ].map(echapper).join(';')),
    ];

    // Le prefixe d'octets fait ouvrir les accents correctement dans un tableur
    // sous Windows, ou le client travaille.
    const fichier = new Blob(['\ufeff' + lignes.join('\r\n')],
                             { type: 'text/csv;charset=utf-8;' });
    const adresse = URL.createObjectURL(fichier);
    const lien = document.createElement('a');
    lien.href = adresse;
    lien.download = 'comptes-omnia-' + new Date().toISOString().slice(0, 10) + '.csv';
    document.body.appendChild(lien);
    lien.click();
    lien.remove();
    URL.revokeObjectURL(adresse);
  };

  // Droits effectivement appliqués par la plateforme, déduits du rôle. La
  // matrice reflète ainsi ce qui est réellement autorisé, et non une liste
  // saisie à la main qui pourrait s'en écarter.
  const droitsDuRole = (role: string): string[] => {
    switch (role) {
      case 'admin':   return ['read', 'write', 'admin', 'export', 'api', 'audit'];
      case 'manager': return ['read', 'write', 'export', 'audit'];
      case 'creator':
      case 'user':    return ['read', 'write'];
      default:        return ['read'];
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'bg-eg-accent/10 text-eg-accent';
      case 'manager': return 'bg-eg-voile text-eg-brun';
      case 'user': return 'bg-eg-valide/10 text-eg-valide';
      default: return 'bg-eg-voile text-eg-encre';
    }
  };

  const getStatusColor = (status: string) => {
    return status === 'active' ? 'text-eg-valide bg-eg-valide/10' : 'text-eg-accent bg-eg-accent/10';
  };

  const getStatusIcon = (status: string) => {
    return status === 'active' ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />;
  };

  const getLicenseColor = (license: string) => {
    switch (license) {
      case 'premium': return 'bg-purple-100 text-purple-800';
      case 'standard': return 'bg-eg-voile text-eg-brun';
      case 'basic': return 'bg-eg-valide/10 text-eg-valide';
      default: return 'bg-eg-voile text-eg-encre';
    }
  };

  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.department.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleUserSelect = (userId: string) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    setSelectedUsers(
      selectedUsers.length === filteredUsers.length
        ? []
        : filteredUsers.map(user => user.id)
    );
  };

  /**
   * Supprime les comptes cochés.
   *
   * Ces trois actions ne touchaient que l'affichage : le compte disparaissait
   * du tableau, ou changeait d'étiquette, sans que rien ne bouge en base. Un
   * responsable croyait avoir ferme un acces qui restait ouvert, et le
   * rechargement de la page faisait reapparaitre le compte supprime.
   */
  const handleDeleteUsers = async () => {
    setActionEnCours(true);
    setMessageAdmin('');
    try {
      for (const id of selectedUsers) {
        await assistantsService.supprimerCompte(id);
      }
      setUsers(users.filter(user => !selectedUsers.includes(user.id)));
      setMessageAdmin(
        `${selectedUsers.length} compte${selectedUsers.length > 1 ? 's supprimés' : ' supprimé'}.`,
      );
      setSelectedUsers([]);
      setConfirmationSuppression(false);
    } catch (e: any) {
      setMessageAdmin(e.message || "La suppression n'a pas abouti.");
    } finally {
      setActionEnCours(false);
    }
  };

  const handleToggleUserStatus = async (userId: string) => {
    setActionEnCours(true);
    setMessageAdmin('');
    try {
      const { is_active } = await assistantsService.basculerCompte(userId);
      setUsers(users.map(user =>
        user.id === userId
          ? { ...user, status: is_active ? 'active' : 'inactive' }
          : user
      ));
    } catch (e: any) {
      setMessageAdmin(e.message || "Le changement d'état n'a pas abouti.");
    } finally {
      setActionEnCours(false);
    }
  };

  /**
   * Réinitialise un mot de passe et affiche le mot de passe provisoire.
   *
   * Il est affiché parce que la messagerie n'est pas configurée : le courriel
   * de bienvenue ne part pas, et un agent qui n'a jamais reçu son mot de passe
   * n'a aucun moyen d'entrer. Le lire à l'écran et le transmettre de vive voix
   * est aujourd'hui la seule façon de lui ouvrir l'accès.
   */
  /** Enregistre la fiche éditée, puis remet la ligne du tableau à jour. */
  const enregistrerUtilisateur = async () => {
    if (!editingUser) return;
    setActionEnCours(true);
    setMessageAdmin('');
    try {
      await assistantsService.modifierCompte(editingUser.id, {
        first_name: editingUser.first_name ?? '',
        last_name: editingUser.last_name ?? '',
        position: editingUser.position ?? '',
        role: editingUser.role,
      });
      const nom = [editingUser.first_name, editingUser.last_name]
        .filter(Boolean).join(' ').trim();
      setUsers(users.map((u) => (
        u.id === editingUser.id
          ? { ...u, name: nom || u.name, role: editingUser.role,
              position: editingUser.position ?? u.position }
          : u
      )));
      setShowUserModal(false);
      setEditingUser(null);
    } catch (e: any) {
      setMessageAdmin(e.message || "La fiche n'a pas pu être enregistrée.");
    } finally {
      setActionEnCours(false);
    }
  };

  const handleResetPassword = async (userId: string, email: string) => {
    setActionEnCours(true);
    setMessageAdmin('');
    setMotDePasseProvisoire(null);
    try {
      const { temporary_password, courriel_envoye } =
        await assistantsService.reinitialiserMotDePasse(userId);
      setMotDePasseProvisoire({
        email,
        motDePasse: temporary_password,
        envoye: courriel_envoye,
      });
    } catch (e: any) {
      setMessageAdmin(e.message || "La réinitialisation n'a pas abouti.");
    } finally {
      setActionEnCours(false);
    }
  };

  return (
    <DashboardLayout>
      {/* Aurora Background Effect */}
      
      <div className="mx-auto max-w-[1100px] space-y-6 content-above-aurora">
        <NavMemoire actif="comptes" />
        {/* En-tête */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-[18px] font-semibold tracking-tight text-eg-encre">Comptes et usage</h2>
            <p className="text-[13px] text-eg-gris">Qui accède à la plateforme, et ce qu'il en fait.</p>
          </div>
          <div className="flex items-center space-x-4">
            <Button 
              variant="outline" 
              icon={<Download className="w-4 h-4" />}
              onClick={exporterLesComptes}
              disabled={users.length === 0}
            >
              Exporter
            </Button>
            <Button 
              variant="primary" 
              icon={<UserPlus className="w-4 h-4" />}
              // La création d'un compte vit dans les paramètres, qui gèrent le
              // mot de passe provisoire et son affichage. Ce bouton ouvrait une
              // fenêtre inexistante : il y conduit désormais plutôt que de ne
              // rien faire.
              onClick={() => { window.location.href = '/workspace/memoire/parametres'; }}
            >
              Nouvel utilisateur
            </Button>
          </div>
        </div>

        {/* Statistiques rapides */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-eg-gris">Utilisateurs Actifs</p>
                  <p className="text-2xl font-bold text-eg-encre">
                    {journalCharge ? (
                      <CountUp to={users.filter(u => u.status === 'active').length} duration={1.5} delay={0.2} className="text-2xl font-bold text-eg-encre" />
                    ) : (
                      <span className="text-eg-estompe">&mdash;</span>
                    )}
                  </p>
                </div>
                <Users className="w-8 h-8 text-eg-accent" />
              </div>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-eg-gris">Comptes administrateurs</p>
                  <p className="text-2xl font-bold text-eg-encre">
                    {journalCharge ? (
                      <CountUp to={users.filter(u => u.role === 'admin').length} duration={1.5} delay={0.4} className="text-2xl font-bold text-eg-encre" />
                    ) : (
                      <span className="text-eg-estompe">&mdash;</span>
                    )}
                  </p>
                </div>
                <Crown className="w-8 h-8 text-purple-600" />
              </div>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-eg-gris">Connexions aujourd'hui</p>
                  <p className="text-2xl font-bold text-eg-encre">
                    {journalCharge ? (
                      <CountUp to={connexionsAujourdHui} duration={1.5} delay={0.6} className="text-2xl font-bold text-eg-encre" />
                    ) : (
                      <span className="text-eg-estompe">&mdash;</span>
                    )}
                  </p>
                </div>
                <Clock className="w-8 h-8 text-eg-valide" />
              </div>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.3 }}
          >
            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-eg-gris">Comptes jamais connectés</p>
                  <p className="text-2xl font-bold text-eg-encre">
                    {journalCharge ? (
                      <CountUp to={jamaisConnectes} duration={1.5} delay={0.8} className="text-2xl font-bold text-eg-encre" />
                    ) : (
                      <span className="text-eg-estompe">&mdash;</span>
                    )}
                  </p>
                </div>
                <AlertTriangle className="w-8 h-8 text-orange-600" />
              </div>
            </Card>
          </motion.div>
        </div>

        {/* Onglets */}
        <div className="border-b border-eg-bord">
          <nav className="-mb-px flex space-x-8">
            {[
              { id: 'users', name: 'Utilisateurs', icon: Users },
              { id: 'usage', name: 'Utilisation', icon: Activity },
              { id: 'whatsapp', name: 'WhatsApp', icon: MessageSquare },
              { id: 'audit', name: 'Audit', icon: FileText }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                  activeTab === tab.id
                    ? 'border-eg-accent text-eg-accent'
                    : 'border-transparent text-eg-gris hover:text-eg-brun hover:border-[#DDE2DD]'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Contenu des onglets */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            {/* Barre de recherche et filtres */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-4">
                    <div className="relative">
                      <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-eg-estompe" />
                      <input
                        type="text"
                        placeholder="Rechercher un utilisateur..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 pr-4 py-2 border border-[#DDE2DD] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-64"
                      />
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {selectedUsers.length > 0 && (
                      <>
                        <span className="text-sm text-eg-gris">{selectedUsers.length} sélectionné(s)</span>
                        <Button
                          variant="outline"
                          icon={<UserMinus className="w-4 h-4" />}
                          onClick={() => setConfirmationSuppression(true)}
                          disabled={actionEnCours}
                        >
                          Supprimer
                        </Button>
                      </>
                    )}
                  </div>
                </div>

                {confirmationSuppression && (
                  <div className="mb-4 rounded-md border border-[#E4B9B9] bg-[#FBF1F1] px-4 py-3">
                    <p className="text-sm text-[#8A2A2A]">
                      Supprimer définitivement {selectedUsers.length} compte
                      {selectedUsers.length > 1 ? 's' : ''} ? La personne perdra l'accès à
                      la plateforme, et ses échanges cesseront de lui être rattachés.
                    </p>
                    <div className="mt-2 flex gap-2">
                      <Button
                        variant="outline"
                        onClick={handleDeleteUsers}
                        disabled={actionEnCours}
                      >
                        Supprimer définitivement
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setConfirmationSuppression(false)}
                      >
                        Annuler
                      </Button>
                    </div>
                  </div>
                )}

                {motDePasseProvisoire && (
                  <div className="mb-4 rounded-md border border-[#DDE2DD] bg-eg-voile px-4 py-3">
                    <p className="text-sm text-eg-encre">
                      Mot de passe provisoire de {motDePasseProvisoire.email} :
                    </p>
                    <p className="mt-1 font-mono text-lg font-semibold text-eg-encre">
                      {motDePasseProvisoire.motDePasse}
                    </p>
                    <p className="mt-1 text-xs text-eg-gris">
                      {motDePasseProvisoire.envoye
                        ? "Il a également été envoyé par courriel."
                        : "La messagerie n'est pas configurée : ce mot de passe n'a pas été "
                          + "envoyé. Transmettez-le à la personne, et invitez-la à le changer "
                          + "dès sa première connexion. Il ne sera plus affiché après "
                          + "fermeture de ce bandeau."}
                    </p>
                    <button
                      type="button"
                      onClick={() => setMotDePasseProvisoire(null)}
                      className="mt-2 text-xs text-eg-accent hover:underline"
                    >
                      Fermer
                    </button>
                  </div>
                )}

                {messageAdmin && (
                  <div className="mb-4 rounded-md border border-[#DDE2DD] bg-white px-4 py-3
                    text-sm text-eg-encre">
                    {messageAdmin}
                  </div>
                )}

                {/* Tableau des utilisateurs */}
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-eg-bord">
                    <thead className="bg-eg-papier">
                      <tr>
                        <th className="px-6 py-3 text-left">
                          <input
                            type="checkbox"
                            checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                            onChange={handleSelectAll}
                            className="rounded border-[#DDE2DD]"
                          />
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-eg-gris uppercase tracking-wider">
                          Utilisateur
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-eg-gris uppercase tracking-wider">
                          Rôle
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-eg-gris uppercase tracking-wider">
                          Statut
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-eg-gris uppercase tracking-wider">
                          Service
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-eg-gris uppercase tracking-wider">
                          Dernière connexion
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-eg-gris uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-eg-blanc divide-y divide-eg-bord">
                      {filteredUsers.map((user) => (
                        <tr key={user.id} className="hover:bg-eg-papier">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <input
                              type="checkbox"
                              checked={selectedUsers.includes(user.id)}
                              onChange={() => handleUserSelect(user.id)}
                              className="rounded border-[#DDE2DD]"
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="w-10 h-10 bg-eg-voile rounded-full flex items-center justify-center">
                                <User className="w-5 h-5 text-eg-accent" />
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-eg-encre">{user.name}</div>
                                <div className="text-sm text-eg-gris">{user.email}</div>
                                <div className="text-xs text-eg-estompe">{user.position}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRoleColor(user.role)}`}>
                              {user.role}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className={`px-3 py-1 rounded-full text-sm flex items-center gap-2 w-fit ${getStatusColor(user.status)}`}>
                              {getStatusIcon(user.status)}
                              {user.status === 'active' ? 'Actif' : 'Inactif'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm text-eg-gris">
                              {user.department || '—'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-eg-gris">
                            {user.lastLogin
                              ? new Date(user.lastLogin).toLocaleString('fr-FR', {
                                  day: '2-digit', month: '2-digit', year: 'numeric',
                                  hour: '2-digit', minute: '2-digit',
                                })
                              : 'Jamais connecté'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center space-x-2">
                              <Button
                                variant="outline"
                                size="sm"
                                icon={<Edit className="w-4 h-4" />}
                                onClick={() => {
                                  // Le journal ne rend qu'un nom complet. On le
                                  // scinde pour préremplir la fiche, sans quoi
                                  // les champs s'ouvriraient vides et
                                  // l'enregistrement effacerait le nom.
                                  const morceaux = (user.name || '').trim().split(/\s+/);
                                  setEditingUser({
                                    ...user,
                                    first_name: morceaux[0] || '',
                                    last_name: morceaux.slice(1).join(' '),
                                  });
                                  setShowUserModal(true);
                                }}
                              >
                                Modifier
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                icon={user.status === 'active' ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                                onClick={() => handleToggleUserStatus(user.id)}
                                disabled={actionEnCours}
                              >
                                {user.status === 'active' ? 'Désactiver' : 'Activer'}
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                icon={<RefreshCw className="w-4 h-4" />}
                                onClick={() => handleResetPassword(user.id, user.email)}
                                disabled={actionEnCours}
                              >
                                Mot de passe
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </motion.div>
          </div>
        )}

        

        {false && (
          <div className="space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="p-6">
                <h3 className="text-lg font-semibold text-eg-encre mb-4">Plans de Licence</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {licenses.map((license) => (
                    <div key={license.id} className={`p-6 border-2 rounded-md ${
                      license.id === 'premium' ? 'border-purple-500 bg-purple-50' : 'border-eg-bord'
                    }`}>
                      <div className="text-center mb-4">
                        <h4 className="text-xl font-bold text-eg-encre">{license.name}</h4>
                        <div className="mt-2">
                          <span className="text-3xl font-bold text-eg-encre">
                            €<CountUp to={license.price} duration={1.5} delay={0.3} className="text-3xl font-bold text-eg-encre" />
                          </span>
                          <span className="text-eg-gris">/mois</span>
                        </div>
                        <p className="text-sm text-eg-gris mt-1">
                          {license.users === -1 ? 'Utilisateurs illimités' : `Jusqu'à ${license.users} utilisateurs`}
                        </p>
                      </div>
                      
                      <div className="space-y-3 mb-6">
                        <h5 className="font-medium text-eg-encre">Modules inclus :</h5>
                        <div className="space-y-1">
                          {license.modules.map((module) => (
                            <div key={module} className="flex items-center text-sm text-eg-gris">
                              <CheckCircle className="w-4 h-4 text-eg-valide mr-2" />
                              {module === 'all' ? 'Tous les modules' : module}
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="space-y-3 mb-6">
                        <h5 className="font-medium text-eg-encre">Fonctionnalités :</h5>
                        <div className="space-y-1">
                          {license.features.map((feature, index) => (
                            <div key={index} className="flex items-center text-sm text-eg-gris">
                              <CheckCircle className="w-4 h-4 text-eg-valide mr-2" />
                              {feature}
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <span className={`px-3 py-1 rounded-full text-sm flex items-center gap-2 ${getStatusColor(license.active ? 'active' : 'inactive')}`}>
                          {getStatusIcon(license.active ? 'active' : 'inactive')}
                          {license.active ? 'Actif' : 'Inactif'}
                        </span>
                        <Button variant="outline" size="sm" icon={<Edit className="w-4 h-4" />}>
                          Modifier
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
              <Card className="p-6">
                <h3 className="text-lg font-semibold text-eg-encre mb-4">Utilisation des Licences</h3>
                <div className="space-y-4">
                  {licenses.map((license) => {
                    // Aucune licence n'est attribuée en base : ce décompte reste à zéro
                    // tant que la notion n'existe pas dans la plateforme.
                    const usersWithLicense = 0;
                    const maxUsers = license.users === -1 ? users.length : license.users;
                    const percentage = license.users === -1 ? 100 : (usersWithLicense / maxUsers) * 100;
                    
                    return (
                      <div key={license.id} className="p-4 border border-eg-bord rounded-md">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-medium text-eg-encre">{license.name}</h4>
                          <span className="text-sm text-eg-gris">
                            {usersWithLicense} / {license.users === -1 ? '∞' : maxUsers} utilisateurs
                          </span>
                        </div>
                        <div className="w-full bg-eg-bord rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${
                              percentage > 80 ? 'bg-eg-accent/100' : percentage > 60 ? 'bg-yellow-500' : 'bg-eg-valide/100'
                            }`}
                            style={{ width: `${Math.min(percentage, 100)}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-eg-gris mt-1">
                          {percentage.toFixed(1)}% d'utilisation
                        </p>
                      </div>
                    );
                  })}
                </div>
              </Card>
            </motion.div>
          </div>
        )}

        {activeTab === 'usage' && <SuiviUtilisation />}

        {activeTab === 'whatsapp' && <CanalWhatsApp />}

        {activeTab === 'audit' && (
          <div className="space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="p-6">
                <h3 className="text-lg font-semibold text-eg-encre mb-4">Logs d'Audit</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-eg-bord">
                    <thead className="bg-eg-papier">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-eg-gris uppercase tracking-wider">
                          Utilisateur
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-eg-gris uppercase tracking-wider">
                          Action
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-eg-gris uppercase tracking-wider">
                          Ressource
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-eg-gris uppercase tracking-wider">
                          Date/Heure
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-eg-gris uppercase tracking-wider">
                          IP
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-eg-gris uppercase tracking-wider">
                          Statut
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-eg-blanc divide-y divide-eg-bord">
                      {auditLogs.map((log) => (
                        <tr key={log.id} className="hover:bg-eg-papier">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-eg-encre">
                            {log.user}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-eg-encre">
                            {log.action}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-eg-gris">
                            {log.resource}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-eg-gris">
                            {new Date(log.timestamp).toLocaleString('fr-FR')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-eg-gris">
                            {log.ip}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className={`px-3 py-1 rounded-full text-sm flex items-center gap-2 w-fit ${getStatusColor(log.status)}`}>
                              {getStatusIcon(log.status)}
                              {log.status === 'success' ? 'Succès' : 'Échec'}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </motion.div>
          </div>
        )}

        
      </div>

      {/*
        Fenêtre d'édition d'un compte.

        Elle n'existait pas : « Modifier » mettait à jour un état que rien ne
        lisait, si bien que le bouton ne produisait aucun effet visible. La
        création d'un compte se fait depuis l'écran des paramètres, qui gère le
        mot de passe provisoire ; on ne la double pas ici.
      */}
      {showUserModal && editingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
            <h2 className="text-lg font-semibold text-eg-encre">
              Modifier {editingUser.name || editingUser.email}
            </h2>
            <p className="mt-1 text-sm text-eg-gris">{editingUser.email}</p>

            <div className="mt-4 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs font-semibold uppercase tracking-wide text-eg-gris">
                    Prénom
                  </span>
                  <input
                    value={editingUser.first_name ?? ''}
                    onChange={(e) => setEditingUser({
                      ...editingUser, first_name: e.target.value,
                    })}
                    className="mt-1 w-full rounded-md border border-[#DDE2DD] px-3 py-2 text-sm
                      focus:border-eg-accent focus:outline-none"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-semibold uppercase tracking-wide text-eg-gris">
                    Nom
                  </span>
                  <input
                    value={editingUser.last_name ?? ''}
                    onChange={(e) => setEditingUser({
                      ...editingUser, last_name: e.target.value,
                    })}
                    className="mt-1 w-full rounded-md border border-[#DDE2DD] px-3 py-2 text-sm
                      focus:border-eg-accent focus:outline-none"
                  />
                </label>
              </div>

              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-wide text-eg-gris">
                  Poste
                </span>
                <input
                  value={editingUser.position ?? ''}
                  onChange={(e) => setEditingUser({
                    ...editingUser, position: e.target.value,
                  })}
                  placeholder="Ex. : Cadre PEV — Conakry"
                  className="mt-1 w-full rounded-md border border-[#DDE2DD] px-3 py-2 text-sm
                    focus:border-eg-accent focus:outline-none"
                />
              </label>

              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-wide text-eg-gris">
                  Rôle
                </span>
                <select
                  value={editingUser.role ?? 'user'}
                  onChange={(e) => setEditingUser({
                    ...editingUser, role: e.target.value,
                  })}
                  className="mt-1 w-full rounded-md border border-[#DDE2DD] px-3 py-2 text-sm
                    focus:border-eg-accent focus:outline-none"
                >
                  <option value="user">Agent</option>
                  <option value="manager">Responsable</option>
                  <option value="admin">Administrateur</option>
                </select>
                <span className="mt-1 block text-xs text-eg-estompe">
                  Attribuer un rôle est un acte d'administration : le serveur le
                  refuse à un compte qui n'en a pas le droit.
                </span>
              </label>
            </div>

            {messageAdmin && (
              <p className="mt-3 text-sm text-[#8A2A2A]">{messageAdmin}</p>
            )}

            <div className="mt-5 flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => { setShowUserModal(false); setMessageAdmin(''); }}
              >
                Annuler
              </Button>
              <Button onClick={enregistrerUtilisateur} disabled={actionEnCours}>
                Enregistrer
              </Button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default AdminPage;

